import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { base_url } from "../utils/axiosConfig";

const TestimonialSection = () => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      city: "",
      testimonial: "",
      rating: 0,
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
        .nullable(),
      city: Yup.string().required("City is required"),
      testimonial: Yup.string()
        .min(10, "Testimonial must be at least 10 characters")
        .required("Testimonial is required"),
      rating: Yup.number()
        .min(1, "Please select a rating")
        .required("Rating is required"),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);

        await axios.post(
          `${base_url}testimonials/createTestimonial`,
          values,
        );

        resetForm();
        alert("Thank you for your testimonial!");
      } catch (error) {
        console.error(error);
        alert("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Banner Image */}
      <div className="w-screen h-64 md:h-[600px] border-[3px] border-[#35A93E]">
        <img
          src="/customersay.jpeg"
          alt="LifeOnPlus Testimonials"
          className="w-full h-full object-contain"
        />
      </div>

      <div className="max-w-6xl w-full space-y-20 pt-24 pb-24">
        {/* Header */}
        <div className="text-center animate-slide-in">
          <h1 className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#35A93E] to-[#2E8C36]">
            Share Your Experience
          </h1>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <h2 className="text-3xl font-bold text-[#35A93E] mb-10">
            Submit Your Testimonial
          </h2>

          <form onSubmit={formik.handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-3 w-full border rounded-xl px-4 py-3"
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="text-red-500 text-sm">{formik.errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-3 w-full border rounded-xl px-4 py-3"
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-sm">{formik.errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <input
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  className="mt-3 w-full border rounded-xl px-4 py-3"
                />
                {formik.errors.phone && (
                  <p className="text-red-500 text-sm">{formik.errors.phone}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  name="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-3 w-full border rounded-xl px-4 py-3"
                />
                {formik.touched.city && formik.errors.city && (
                  <p className="text-red-500 text-sm">{formik.errors.city}</p>
                )}
              </div>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rating
              </label>
              <div className="mt-3 flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    onClick={() => formik.setFieldValue("rating", star)}
                    className={`text-3xl ${
                      formik.values.rating >= star
                        ? "text-yellow-400"
                        : "text-gray-300"
                    }`}
                  >
                    ★
                  </button>
                ))}
              </div>
              {formik.errors.rating && (
                <p className="text-red-500 text-sm">{formik.errors.rating}</p>
              )}
            </div>

            {/* Testimonial */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Testimonial
              </label>
              <textarea
                name="testimonial"
                rows="5"
                value={formik.values.testimonial}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-3 w-full border rounded-xl px-4 py-3"
              />
              {formik.touched.testimonial && formik.errors.testimonial && (
                <p className="text-red-500 text-sm">
                  {formik.errors.testimonial}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-[#35A93E] text-white"
            >
              {loading ? "Submitting..." : "Submit Testimonial"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TestimonialSection;
